import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Camera, CameraOff, Check, Keyboard, MapPin, Wine as WineIcon, Coins, PackageX } from "lucide-react";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { LocationSelect } from "@/components/LocationSelect";
import { useScanner } from "@/components/useScanner";

export default function Scan() {
  const [item, setItem] = useState(null);
  const [manual, setManual] = useState("");
  const [loc, setLoc] = useState("");
  const [done, setDone] = useState(null);
  const navigate = useNavigate();

  const lookup = async (raw) => {
    const m = String(raw).match(/\/[bq]\/([^/?#\s]+)/i);
    const code = (m ? decodeURIComponent(m[1]) : String(raw)).toUpperCase();
    setDone(null);
    try {
      const { data } = await api.get(`/scan/${encodeURIComponent(code.trim())}`);
      if (data.type === "box") {
        scanner.stop?.();
        return navigate(`/b/${encodeURIComponent(data.barcode)}`);
      }
      setItem(data);
      setLoc("");
      if (navigator.vibrate) navigator.vibrate(80);
    } catch (e) {
      setItem(null);
      toast.error(errMsg(e));
    }
  };
  const scanner = useScanner(lookup);
  const [params] = useSearchParams();
  useEffect(() => {
    const c = params.get("code");
    if (c) lookup(c);
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps
  const startCamera = () => {
    setItem(null);
    setDone(null);
    setTimeout(scanner.start, 60);
  };
  const collapsed = !scanner.on && (item || done);

  const confirm = async (action, extra) => {
    try {
      const { data } = action === "move"
        ? await api.post("/moves", { barcode: item.barcode, location_id: loc })
        : await api.post("/bottles/action", { barcode: item.barcode, action, ...extra });
      setDone(data);
      setItem(null);
      setManual("");
      toast.success(action === "move" ? `Moved to ${data.to_location}` : `Recorded as ${data.to_location.toLowerCase()}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="max-w-lg mx-auto px-3 sm:px-4 py-6 sm:py-12">
      <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-2">Move a bottle or box</p>
      <h2 className="font-serif text-4xl text-[#F5F0EB] mb-6">Scan</h2>

      <div className={`relative aspect-square w-full max-h-[75vh] rounded-xl overflow-hidden border border-[#2E252A] bg-[#110D0F] mb-4 ${collapsed ? "hidden" : ""}`}>
        <div id="qr-reader" className="absolute inset-0" data-testid="camera-view" />
        {!scanner.on && (
          <div className="absolute inset-0 grid place-items-center text-center px-6">
            <div>
              <Camera className="w-10 h-10 mx-auto text-[#4A3B43] mb-3" strokeWidth={1} />
              <p className="text-sm text-[#6E665D]">Point the camera at the QR code label, about 10–15 cm away</p>
            </div>
          </div>
        )}
        {scanner.on && <div className="laser absolute left-[8%] right-[8%] h-0.5 bg-[#8C2B36] shadow-[0_0_12px_#8C2B36] pointer-events-none" />}
      </div>
      <Button onClick={scanner.on ? scanner.stop : startCamera} className="w-full h-14 text-base bg-[#5C1D24] hover:bg-[#8C2B36] text-[#F5F0EB] mb-4" data-testid="start-camera-button">
        {scanner.on ? <><CameraOff className="w-5 h-5 mr-2" />Stop camera</> : <><Camera className="w-5 h-5 mr-2" />{collapsed ? "Scan another" : "Start camera"}</>}
      </Button>

      <form onSubmit={(e) => { e.preventDefault(); if (manual.trim()) lookup(manual); }} className="flex gap-2 mb-8">
        <div className="relative flex-1">
          <Keyboard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6E665D]" />
          <Input value={manual} onChange={(e) => setManual(e.target.value)} placeholder="Or type the code, e.g. BTL-000001" className="pl-10 h-12 font-mono bg-[#161214]" data-testid="manual-barcode-input" />
        </div>
        <Button type="submit" variant="outline" className="h-12" data-testid="manual-barcode-submit">Find</Button>
      </form>

      {item && <ScannedItem key={item.barcode} item={item} loc={loc} setLoc={setLoc} onConfirm={confirm} />}
      {done && (
        <div className="rounded-xl border border-[#2E5D4B] bg-[#2E5D4B]/15 p-5 rise" data-testid="move-success">
          <p className="flex items-center gap-2 text-[#F5F0EB]"><Check className="w-4 h-4 text-emerald-400" /> {done.barcode} {done.action === "move" ? "moved to" : "→"} <b>{done.to_location}</b></p>
          <p className="font-mono text-xs text-[#A89F91] mt-2" data-testid="move-success-timestamp">Recorded {fmtDateTime(done.moved_at)}</p>
        </div>
      )}
    </div>
  );
}

const ACTIONS = [
  { key: "move", label: "Change location", icon: MapPin },
  { key: "consumed", label: "Consumed", icon: WineIcon },
  { key: "sold", label: "Sale", icon: Coins },
  { key: "lost", label: "Lost", icon: PackageX },
];

const ScannedItem = ({ item, loc, setLoc, onConfirm }) => {
  const inCellar = item.type === "box" || item.status === "in_cellar";
  const [action, setAction] = useState("move");
  const [note, setNote] = useState("");
  const [price, setPrice] = useState("");
  const [saleId, setSaleId] = useState("");
  const [saleBox, setSaleBox] = useState("");
  const [sales, setSales] = useState([]);
  useEffect(() => {
    if (action === "sold") {
      api.get("/sales").then((r) => setSales(r.data.filter((s) => !s.closed))).catch(() => setSales([]));
    }
  }, [action]);
  const available = item.type === "box" || !inCellar ? ACTIONS.slice(0, 1) : ACTIONS;
  const submit = () => onConfirm(action, { note, sale_price: action === "sold" && price !== "" ? Number(price) : null, sale_id: action === "sold" ? saleId || null : null, sale_box_number: action === "sold" ? saleBox : "" });
  return (
    <div className="rounded-xl border border-[#4A3B43] bg-[#1E181B] p-5 rise" data-testid="scanned-barcode-result">
      <p className="font-mono text-xs text-[#D4AF37]">{item.type === "box" ? "BOX" : "BOTTLE"} · {item.barcode}</p>
      <p className="font-serif text-2xl text-[#F5F0EB] mt-1" data-testid="scanned-item-label">{item.label}</p>
      {item.type === "box" && <p className="text-sm text-[#A89F91] mt-1">{item.bottle_count} bottle{item.bottle_count === 1 ? "" : "s"} · {item.wines.join(", ")}</p>}
      {item.type === "bottle" && item.box_number && <p className="text-sm text-[#A89F91] mt-1">In box {item.box_number} (moving it alone takes it out of the box)</p>}
      {!inCellar && <p className="text-sm text-[#E5A93C] mt-1" data-testid="scanned-status">This bottle is recorded as {STATUS_TXT[item.status]}. Choose a location to put it back in the cellar.</p>}
      <p className="text-sm text-[#A89F91] mt-4">Current location</p>
      <p className="text-[#F5F0EB] mb-4" data-testid="scanned-current-location">{item.location || "Unassigned"}</p>
      {available.length > 1 && (
        <div className="grid grid-cols-2 gap-2 mb-4">
          {available.map((a) => (
            <button key={a.key} onClick={() => setAction(a.key)} data-testid={`scan-action-${a.key}`}
              className={`flex items-center justify-center gap-2 h-12 rounded-md border text-sm transition-colors ${action === a.key ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]" : "border-[#2E252A] text-[#A89F91] hover:border-[#4A3B43]"}`}>
              <a.icon className="w-4 h-4" strokeWidth={1.5} /> {a.label}
            </button>
          ))}
        </div>
      )}
      {action === "move" ? (
        <LocationSelect value={loc} onChange={setLoc} testId="new-location-select" placeholder="Choose the new location" className="h-12 bg-[#0D0A0B]" />
      ) : (
        <div className="space-y-2">
          {action === "sold" && (
            <>
              <Select value={saleId || "none"} onValueChange={(v) => setSaleId(v === "none" ? "" : v)}>
                <SelectTrigger className="h-12 bg-[#0D0A0B]" data-testid="sale-select"><SelectValue placeholder="Choose a sale" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No sale selected</SelectItem>
                  {sales.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`sale-option-${s.id}`}>{s.name}{s.buyer ? ` · ${s.buyer}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
              {!sales.length && <p className="text-xs text-[#6E665D]" data-testid="no-sales-hint">No open sale yet — the owner can create them on the Sales page.</p>}
              <Input value={saleBox} onChange={(e) => setSaleBox(e.target.value)} maxLength={40} placeholder="Sale box number (optional)" className="h-12 bg-[#0D0A0B]" data-testid="sale-box-number-input" />
            </>
          )}
          {action === "sold" && <Input type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="Sale price (optional)" className="h-12 bg-[#0D0A0B]" data-testid="sale-price-input" />}
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={action === "sold" ? "Buyer / note (optional)" : action === "lost" ? "What happened? (optional)" : "Occasion / tasting note (optional)"} className="h-12 bg-[#0D0A0B]" data-testid="action-note-input" />
        </div>
      )}
      <Button disabled={action === "move" && (!loc || (inCellar && loc === item.location_id))} onClick={submit} className="w-full h-14 mt-4 text-base bg-[#D4AF37] hover:bg-[#E5A93C] text-[#0D0A0B]" data-testid="confirm-relocation-button">
        {CONFIRM_TXT[action]}
      </Button>
    </div>
  );
};

const STATUS_TXT = { consumed: "consumed", sold: "sold", lost: "lost" };
const CONFIRM_TXT = { move: "Confirm new location", consumed: "Confirm consumed", sold: "Confirm sale", lost: "Confirm lost" };
